import {
    Button,
    CloseIcon,
    Divider,
    EditIcon,
    Flex,
    FlexItem,
    Header,
    InputProps,
    Loader,
    SaveIcon,
    TrashCanIcon
} from "@fluentui/react-northstar";
import languageObject from "../../../translations";
import * as React from "react";
import {useCallback, useContext, useEffect, useState} from "react";
import {AppContext, IAppContextState} from "../../../services/AppContext";
import {MailingList} from "../../../Interfaces/MailingList";
import {useNavigate} from "react-router-dom";
import ConfirmDialog from "../../Dialogs/ConfirmDialog";
import {TitleInput} from "./TitleInput";
import {EmailsGrid} from "./EmailsGrid/EmailsGrid";

type State = {
    list: MailingList | undefined,
    showConfirmDelete: boolean,
    isDeleting: boolean,
    isSaving: boolean,
}

const initialState: State = {
    list: undefined,
    showConfirmDelete: false,
    isDeleting: false,
    isSaving: false,
}

export const EditMailingList = () => {
    const navigate = useNavigate();
    const context = useContext<IAppContextState>(AppContext);
    const [state, setState] = useState<State>({...initialState});

    useEffect(() => {
        const listId = window.location.pathname.split("/")[2];
        const list = context.usersLists.lists.find(l => l.id === listId);
        if (state.list || !list) return;
        setState((prevState): State => ({...prevState, list}));
    }, [context.usersLists.lists]);

    const handleClose = useCallback(() => {
        navigate("/users-lists");
    }, []);

    const handleSave = useCallback(async () => {
        if (!state.list) return;
        setState((prevState): State => ({...prevState, isSaving: true}));
        await context.usersLists.addUpdateUsersList(state.list);
        setState((prevState): State => ({...prevState, isSaving: false}));
        navigate("/users-lists");
    }, [state.list]);

    const handleCancelDeleteList = useCallback(() => {
        setState((prevState): State => ({...prevState, showConfirmDelete: false}));
    }, []);

    const handleDelete = useCallback(() => {
        setState((prevState): State => ({...prevState, showConfirmDelete: true}));
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!state.list?.id) return;
        setState(prevState => ({...prevState, isDeleting: true}));
        await context.usersLists.deleteUsersList(state.list.id);
        navigate("/users-lists");
    }, [state.list?.id]);

    const handleChangeTitle = useCallback((_: React.SyntheticEvent, data: InputProps | undefined) => {
        const value = (data?.value ?? "") + "";
        setState((prevState): State => {
            if (!prevState.list) return prevState;
            return {...prevState, list: {...prevState.list, name: value}}
        });
    }, []);

    const handleEmailsChange = useCallback((emails: Array<string>) => {
        setState((prevState): State => {
            if (!prevState.list) return prevState;
            return {...prevState, list: {...prevState.list, emails}}
        });
    }, []);

    return (
        <Flex column fill gap="gap.medium" className={"p-20px overflow-hidden"}>
            {renderHeader({
                ...state,
                handleDelete,
                handleClose,
                handleSave
            })}
            <Flex className={"overflow-hidden"} fill vAlign={"center"} hAlign={"center"}>
                {!state.list ? <Loader/> : renderContent({
                    ...state,
                    handleChangeTitle,
                    handleEmailsChange,
                })}
            </Flex>
            <ConfirmDialog
                show={state.showConfirmDelete}
                title={languageObject.translate("RemoveMailingList")}
                subtitle={state.list?.name}
                content={languageObject.translate("ConfirmRemoveMailingList")}
                onCancel={handleCancelDeleteList}
                onConfirm={handleConfirmDelete}
                loading={state.isDeleting}
                button={{
                    icon: <TrashCanIcon outline/>,
                    title: languageObject.translate("Remove"),
                    className: "delete_button",
                }}
            />
        </Flex>
    );
}

const renderHeader = (data: {
    list: MailingList | undefined,
    handleDelete: () => void,
    handleClose: () => void,
    handleSave: () => void,
    isSaving: boolean,
}) => {
    const {list, handleDelete, handleClose, handleSave, isSaving} = data;
    return (
        <Flex gap={"gap.small"}>
            <Flex gap={"gap.small"} vAlign={"center"}>
                <EditIcon styles={{color: "#782753"}} size={"large"}/>
                <Header as={"h2"} styles={{margin: "0"}}
                        content={languageObject.translate("UpdateMailingList")}/>
            </Flex>
            <FlexItem push>
                <Flex gap={"gap.small"}>
                    <Button
                        primary
                        className={(!list || isSaving) ? "" : "delete_button"}
                        icon={<TrashCanIcon outline/>}
                        content={languageObject.translate("Delete")}
                        disabled={!list || isSaving}
                        onClick={handleDelete}
                    />
                    <Button
                        primary
                        icon={<SaveIcon size={"large"} outline/>}
                        content={languageObject.translate("Save")}
                        disabled={!list || isSaving}
                        loading={isSaving}
                        onClick={handleSave}
                    />
                    <Button
                        icon={<CloseIcon outline/>}
                        content={languageObject.translate("CloseText")}
                        onClick={handleClose}
                        disabled={isSaving}
                    />
                </Flex>
            </FlexItem>
        </Flex>
    )
}

const renderContent = (data: {
    list: MailingList | undefined,
    handleChangeTitle: (e: React.SyntheticEvent, data: InputProps | undefined) => void,
    handleEmailsChange: (emails: Array<string>) => void,
    isSaving: boolean,
}) => {
    const {list, handleChangeTitle, handleEmailsChange, isSaving} = data;
    return (
        <Flex fill column gap={"gap.small"} className={"overflow-hidden"}>
            <TitleInput
                title={list?.name ?? ""}
                onChangeTitle={handleChangeTitle}
                disabled={isSaving}
            />
            <Divider content={languageObject.translate("Users")}/>
            <EmailsGrid
                emails={list?.emails}
                onChange={handleEmailsChange}
                disabled={isSaving}
            />
        </Flex>
    )
}